import { useState } from 'react';
import Layout from '@/components/Layout';
import Breadcrumb from '@/components/Layout/Breadcrumb';
import WrapperContent from '@/components/Layout/WrapperContent';
import CompanySearch from '@/components/Listing/CompaniesList/CompanySearch';
import { Company } from '@/components/Listing/CompaniesList';
import { NavBarContent } from '@/components/Navbar';
import { Metatags } from '@/v2/components/metatags';
import { api } from '@/services/api';
import { api as apiv2 } from '@/v2/services/api';
import { NavBarContentData } from '@/shared/types/home.types';
import { mglGray } from '@/styles/designSystemColors';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { GetServerSideProps } from 'next';

export interface CompanyListingProps {
  responseData: {
    status: number;
    data: {
      nome: string;
      itens: Company[];
      paginacao: string | null;
    };
  };
  navBarData: NavBarContent;
  ogURL: string;
}

export default function CompanyListing({ responseData, navBarData, ogURL }: CompanyListingProps) {
  const [companies, setCompanies] = useState<Company[]>(responseData.data.itens);
  const [nextCursor, setNextCursor] = useState<string | null>(responseData.data.paginacao);
  const [loading, setLoading] = useState(false);

  const handleLoadMore = async () => {
    if (!nextCursor || loading) return;

    setLoading(true);
    try {
      const { data } = await apiv2.get('/empresa/', {
        params: {
          cursor: nextCursor,
        },
      });

      const newItems = data.data.itens;
      const newCursor = data.data.paginacao;

      setCompanies((prev) => [...prev, ...newItems]);
      setNextCursor(newCursor);
    } catch (error) {
      console.error("Erro ao carregar mais empresas", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout layoutData={{ navBarData, tagsData: null }}>
      <Metatags
        titulo="Empresas de Tecnologia de A a Z - Canaltech"
        descricao="Conheça no Canaltech o catálogo de empresas de tecnologia de A a Z, contando um pouco sobre onde e por quem foram fundadas, além da história de cada uma delas."
        robots={{ index: false, maxImagePreview: 'none' }}
        ogURL={ogURL}
      />

      <WrapperContent hasBreadcrumb backgroundColor={mglGray.mglGray200}>
        <Breadcrumb breadcrumbList={[]} />

        <CompanySearch
          companies={companies}
        />

        {nextCursor ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '20px',
            paddingBottom: '150px',
            position: 'relative',
            zIndex: 10
          }}>
            <button
              onClick={handleLoadMore}
              disabled={loading}
              style={{
                padding: '10px 20px',
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            >
              {loading ? 'Carregando...' : 'Carregar mais empresas'}
            </button>
          </div>
        ) : (
          <div style={{ height: '150px', width: '100%', display: 'block', clear: 'both' }} />
        )}
      </WrapperContent>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;

  const mainReq = { promise: apiv2.get(`/empresa/`), link: `/empresa/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;

  const responseData = data;

  mainReq.promise.then(req => {
    if (req.headers) {
      context.res.setHeader('cache-control', req.headers['cache-control'] || 'public, max-age=120');
    }
  });

  context.res.statusCode = statusCode;
  return {
    props: {
      responseData,
      navBarData,
      ogURL: `https://${host}${context.req.url}`,
    },
  };
};
